// JavaScript module to convert SCSS mixins to @emotion/react css styles
import { css } from '@emotion/react';
import Mixin from '../../../util/mixin';
import { FontSize } from '../vendor/_rfs.mixin';
import { colorYiq, escapeSVG } from '../_functions';
import { math, rgba, lighten } from 'polished';
import { BorderRadius } from './_border-radius.mixin';
import { GradientBg } from './_gradients.mixin';

// Form control focus state
//
// Generate a customized focus state and for any input with the specified color,
// which defaults to the `$input-focus-border-color` variable.
//
// We highly encourage you to not customize the default value, but instead use
// this to tweak colors on an as-needed basis. This aesthetic change is based on
// WebKit's default styles, but applicable to a wider range of browsers. Its
// usability and accessibility should be taken into account with any change.
//
// Example usage: change the default blue border and shadow to white for better
// contrast against a dark gray background.
export class FormControlFocus extends Mixin {
  style({ $ }, [ignoreWarning = false]) {
    // @include deprecate("The `form-control-focus()` mixin", "v4.4.0", "v5", ignoreWarning);

    const shadows = $.enableShadows
      ? new BoxShadow($.inputBoxShadow, $.inputFocusBoxShadow).include($)
      : // Avoid using mixin so we can pass custom focus shadow properly
        css`
          box-shadow: ${$.inputFocusBoxShadow};
        `;

    return css`
      &:focus {
        color: $input-focus-color;
        background-color: $input-focus-bg;
        border-color: $input-focus-border-color;
        outline: 0;
      }
    `;
    // ${shadows}
  }
}

// This mixin uses an `if()` technique to be compatible with Dart Sass
// See https://github.com/sass/sass/issues/1873#issuecomment-152293725 for more details
export class FormValidationStateSelector extends Mixin {
  style({ $, content }, [$state]) {
    if ($state === 'valid' || $state === 'invalid') {
      return css`
        .was-validated ${'&' ? '&' : ''}:${$state},
        ${'&' ? '&' : ''}.is-${$state} {
          ${content};
        }
      `;
    } else {
      return css`
        ${'&' ? '&' : ''}.is-${$state} {
          ${content};
        }
      `;
    }
  }
}

export class FormValidationState extends Mixin {
  style({ $ }, [$state, $color, $icon]) {
    const rules = [];

    rules.push(css`
      .${$state}-feedback {
        display: none;
        width: 100%;
        margin-top: $form-feedback-margin-top;
        ${new FontSize($.formFeedbackFontSize).include($)}
        color: ${$color};
      }
    `);

    rules.push(css`
      .${$state}-tooltip {
        position: absolute;
        top: 100%;
        left: 0;
        z-index: 5;
        display: none;
        max-width: 100%; // Contain to parent when possible
        padding: ${$.formFeedbackTooltipPaddingY} ${$.formFeedbackTooltipPaddingX};
        margin-top: 0.1rem;
        ${new FontSize($.formFeedbackTooltipFontSize).include($)};
        line-height: ${$.formFeedbackTooltipLineHeight};
        color: ${colorYiq($, $color)};
        background-color: ${rgba($color, $.formFeedbackTooltipOpacity)};
        ${new BorderRadius($.formFeedbackTooltipBorderRadius).include($)};

        // See https://github.com/twbs/bootstrap/pull/31557
        // Align tooltip to form elements
        .form-row > .col > &,
        .form-row > [class*='col-'] > & {
          left: ${math(`${$.formGridGutterWidth} * 0.5`)};
        }
      }
    `);

    rules.push(css`
      ${new FormValidationStateSelector($state).include(
        $,
        css`
          ~ .#{$state}-feedback,
          ~ .#{$state}-tooltip {
            display: block;
          }
        `
      )}
    `);

    rules.push(css`
      .form-control {
        ${new FormValidationStateSelector($state).include(
          $,
          css`
            border-color: $color;

            ${
              $.enableValidationIcons
                ? css`
                    padding-right: ${$.inputHeightInner} !important; // stylelint-disable-line declaration-no-important
                    background-image: ${escapeSVG($icon)};
                    background-repeat: no-repeat;
                    background-position: right ${$.inputHeightInnerQuarter} center;
                    background-size: ${$.inputHeightInnerHalf} ${$.inputHeightInnerHalf};
                  `
                : css``
            }

            &:focus {
              border-color: $color;
              // box-shadow: 0 0 0 ${$.inputFocusWidth} ${rgba($color, 0.25)};
            }
          `
        )}
      }
    `);

    rules.push(css`
      // stylelint-disable-next-line selector-no-qualifying-type
      select.form-control {
        ${$.enableValidationIcons
          ? new FormValidationStateSelector($state).include(
              $,
              css`
                // stylelint-disable-line declaration-no-important
                padding-right: ${math(`${$.inputPaddingX} * 4`)} !important;
                background-position: right ${math(`${$.inputPaddingX} * 2`)} center;
              `
            )
          : css``}
      }
    `);

    // stylelint-disable-next-line selector-no-qualifying-type
    rules.push(css`
      textarea.form-control {
        ${$.enableValidationIcons
          ? new FormValidationStateSelector($state).include(
              $,
              css`
                padding-right: ${$.inputHeightInner};
                background-position: top ${$.inputHeightInnerQuarter} right ${$.inputHeightInnerQuarter};
              `
            )
          : css``}
      }
    `);

    rules.push(css`
      .custom-select {
        ${new FormValidationStateSelector($state).include(
          $,
          css`
            border-color: ${$color};

            ${
              $.enableValidationIcons
                ? css`
                    // stylelint-disable-line declaration-no-important
                    padding-right: ${$.customSelectFeedbackIconPaddingRight} !important;
                    background: ${$.customSelectBackground},
                      ${$.customSelectBg} ${escapeSVG($icon)} ${$.customSelectFeedbackIconPosition} / ${$.customSelectFeedbackIconSize} no-repeat;
                  `
                : css``
            }

            &:focus {
              border-color: ${$color};
              // box-shadow: 0 0 0 ${$.inputFocusWidth} ${rgba($color, 0.25)};
            }
          `
        )}
      }
    `);

    rules.push(css`
      .form-check-input {
        ${new FormValidationStateSelector($state).include(
          $,
          css`
            ~ .form-check-label {
              color: ${$color};
            }

            ~ .${$state}-feedback, ~ .${$state}-tooltip {
              display: block;
            }
          `
        )}
      }
    `);

    rules.push(css`
      .custom-control-input {
        ${new FormValidationStateSelector($state).include(
          $,
          css`
            ~ .custom-control-label {
              color: ${$color};

              &::before {
                border-color: ${$color};
              }
            }

            &:checked {
              ~ .custom-control-label::before {
                border-color: ${lighten(0.1, $color)};
                ${new GradientBg(lighten(0.1, $color)).include($)};
              }
            }

            &:focus {
              ~ .custom-control-label::before {
                // box-shadow: 0 0 0 ${$.inputFocusWidth} ${rgba($color, 0.25)};
              }

              &:not(:checked) ~ .custom-control-label::before {
                border-color: ${$color};
              }
            }
          `
        )}
      }
    `);

    // custom file
    rules.push(css`
      .custom-file-input {
        ${new FormValidationStateSelector($state).include(
          $,
          css`
            ~ .custom-file-label {
              border-color: ${$color};
            }

            &:focus {
              ~ .custom-file-label {
                border-color: ${$color};
                // box-shadow: 0 0 0 ${$.inputFocusWidth} ${rgba($color, 0.25)};
              }
            }
          `
        )}
      }
    `);

    return css(rules);
  }
}
